import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getPedidoById, Pedido, getEstadoLabel, getEstadoPagoLabel } from '../../services/pedidos';

const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const { pedidoId } = useParams<{ pedidoId: string }>();
  const [loading, setLoading] = useState(true);
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cargarPedido = async () => {
      if (!pedidoId) {
        setError('No se recibió ID de pedido');
        setLoading(false);
        return;
      }

      try {
        const pedidoData = await getPedidoById(parseInt(pedidoId));
        setPedido(pedidoData);
      } catch (error: any) {
        console.error('Error al cargar pedido:', error);
        setError(error?.response?.data?.error || 'Error al cargar los detalles del pedido');
      } finally {
        setLoading(false);
      }
    };

    cargarPedido();
  }, [pedidoId]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#2e5a33' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ minHeight: '100vh', py: 8 }}>
        <Card sx={{ borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ textAlign: 'center', p: 6 }}>
            <ErrorOutlineIcon sx={{ fontSize: 100, color: '#f44336', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2 }}>
              Error
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
                },
              }}
            >
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!pedido) {
    return (
      <Container maxWidth="sm" sx={{ minHeight: '100vh', py: 8 }}>
        <Card sx={{ borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ textAlign: 'center', p: 6 }}>
            <ErrorOutlineIcon sx={{ fontSize: 100, color: '#ff9800', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 2 }}>
              Pedido no encontrado
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
                },
              }}
            >
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', py: 8 }}>
      <Card sx={{ borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ textAlign: 'center', p: 6 }}>
          <CheckCircleIcon sx={{ fontSize: 100, color: '#4caf50', mb: 3 }} />
          
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e5a33', mb: 2 }}>
            ¡Pedido Creado Exitosamente!
          </Typography>
          
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
            Tu pedido ha sido registrado correctamente. Recibirás una confirmación pronto.
          </Typography>

          <Box sx={{ mb: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: '15px' }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Número de Pedido
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: '#2e5a33', mb: 2 }}>
              {pedido.numero_pedido}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Estado: <strong>{getEstadoLabel(pedido.estado)}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Estado de Pago: <strong>{getEstadoPagoLabel(pedido.pagado)}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total: <strong>${parseFloat(pedido.total).toLocaleString('es-CL')}</strong>
              </Typography>
            </Box>

            {pedido.items && pedido.items.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Productos:
                </Typography>
                <List dense>
                  {pedido.items.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={item.producto_nombre || `Producto #${item.producto}`}
                        secondary={`Cantidad: ${item.cantidad} x $${parseFloat(item.precio_unitario).toLocaleString('es-CL')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/mi-cuenta')}
              sx={{
                background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
                },
              }}
            >
              Ver Mis Pedidos
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                borderColor: '#2e5a33',
                color: '#2e5a33',
                '&:hover': {
                  borderColor: '#1a3d1e',
                  bgcolor: 'rgba(46, 90, 51, 0.04)',
                },
              }}
            >
              Volver al Inicio
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CheckoutSuccess;
